import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, InputAdornment, Grid, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel,
  Select, MenuItem, Chip, CircularProgress, Pagination, Autocomplete, Table,
  TableHead, TableRow, TableCell, TableBody, IconButton, Tooltip
} from '@mui/material';
import { Add, Search, Edit, Delete, Inventory, QrCode } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { spAPI } from '../../services/api';
import { StatusChip } from '../../components/common/DataTable';
import { toast } from 'react-toastify';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

const ProductForm = ({ open, onClose, product, categories, onSaved }) => {
  const { register, handleSubmit, reset, control, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);
  useEffect(() => { reset(product || { unit_of_measure: 'piece', reorder_level: 5 }); }, [product, open, reset]);
  const onSubmit = async (data) => {
    try {
      setLoading(true);
      if (product?.id) { await spAPI.updateProduct(product.id, data); toast.success('Part updated'); }
      else { await spAPI.createProduct(data); toast.success('Part added to catalog'); }
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to save'); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle fontWeight={700}>{product?.id ? 'Edit Part' : 'Add New Part'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}><TextField fullWidth label="Part Name *" {...register('name', { required: true })} error={!!errors.name} /></Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select {...register('category_id')} label="Category" defaultValue="">
                  <MenuItem value="">— None —</MenuItem>
                  {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4}><TextField fullWidth label="Brand / Make" {...register('brand')} /></Grid>
            <Grid item xs={6} sm={4}><TextField fullWidth label="Part Number" {...register('part_number')} /></Grid>
            <Grid item xs={6} sm={4}><TextField fullWidth label="Barcode" {...register('barcode')} InputProps={{ startAdornment: <QrCode sx={{ mr: 1, color: 'text.disabled', fontSize: 18 }} /> }} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Compatible Models" {...register('compatible_models')} helperText="e.g. Toyota Camry 2018-2022, Honda Civic 2019+" /></Grid>
            <Grid item xs={4}><TextField fullWidth label="Cost Price *" type="number" {...register('cost_price', { required: true, valueAsNumber: true })} InputProps={{ startAdornment: <span style={{ marginRight: 4 }}>$</span> }} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="Selling Price *" type="number" {...register('selling_price', { required: true, valueAsNumber: true })} InputProps={{ startAdornment: <span style={{ marginRight: 4 }}>$</span> }} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="Wholesale Price" type="number" {...register('wholesale_price', { valueAsNumber: true })} InputProps={{ startAdornment: <span style={{ marginRight: 4 }}>$</span> }} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="Unit of Measure" {...register('unit_of_measure')} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="Reorder Level" type="number" {...register('reorder_level', { valueAsNumber: true })} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="Shelf / Bin Location" {...register('location_code')} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Description" multiline rows={2} {...register('description')} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading}
            sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>
            {loading ? <CircularProgress size={20} /> : (product?.id ? 'Update' : 'Add Part')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const SparePartsProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const LIMIT = 20;

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await spAPI.getProducts({ page, limit: LIMIT, search: search || undefined, category_id: catFilter || undefined });
      setProducts(data.data || []);
      setTotal(data.pagination?.total || 0);
    } catch { toast.error('Failed to load parts'); }
    finally { setLoading(false); }
  }, [page, search, catFilter]);

  useEffect(() => { fetchProducts(); spAPI.getCategories().then(r => setCategories(r.data.data || [])); }, [fetchProducts]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this part?')) return;
    try { await spAPI.deleteProduct(id); toast.success('Deleted'); fetchProducts(); }
    catch { toast.error('Failed to delete'); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Parts Catalog</Typography>
          <Typography variant="body2" color="text.secondary">{total} parts in catalog</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditProduct(null); setFormOpen(true); }}
          sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>
          Add Part
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField placeholder="Search by name, SKU, part #, barcode..." size="small" sx={{ minWidth: 320 }}
          value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Category</InputLabel>
          <Select label="Category" value={catFilter} onChange={e => { setCatFilter(e.target.value); setPage(1); }}>
            <MenuItem value="">All Categories</MenuItem>
            {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box> : (
        <Card>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>SKU</TableCell><TableCell>Part Name</TableCell>
                <TableCell>Brand</TableCell><TableCell>Category</TableCell>
                <TableCell>Part #</TableCell><TableCell align="right">Cost</TableCell>
                <TableCell align="right">Price</TableCell><TableCell align="right">Stock</TableCell>
                <TableCell>Location</TableCell><TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map(p => (
                <TableRow key={p.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 600 }}>{p.sku}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                    {p.compatible_models && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.compatible_models}</Typography>}
                  </TableCell>
                  <TableCell>{p.brand || '—'}</TableCell>
                  <TableCell>{p.category_name || '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{p.part_number || '—'}</TableCell>
                  <TableCell align="right">{fmt(p.cost_price)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#d97706' }}>{fmt(p.selling_price)}</TableCell>
                  <TableCell align="right">
                    <Chip label={p.stock_qty} size="small"
                      color={p.stock_qty <= 0 ? 'error' : p.stock_qty <= p.reorder_level ? 'warning' : 'success'} />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.78rem' }}>{p.location_code || '—'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditProduct(p); setFormOpen(true); }}><Edit fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(p.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {!products.length && <TableRow><TableCell colSpan={10} align="center"><Box sx={{ py: 5 }}><Inventory sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} /><Typography color="text.secondary">No parts found</Typography></Box></TableCell></TableRow>}
            </TableBody>
          </Table>
        </Card>
      )}

      {total > LIMIT && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination count={Math.ceil(total / LIMIT)} page={page} onChange={(_, v) => setPage(v)}
            sx={{ '& .MuiPaginationItem-root.Mui-selected': { bgcolor: '#f59e0b', color: '#fff' } }} />
        </Box>
      )}

      <ProductForm open={formOpen} onClose={() => setFormOpen(false)}
        product={editProduct} categories={categories} onSaved={fetchProducts} />
    </Box>
  );
};

export default SparePartsProducts;
